import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './InsuranceContractTypesDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import { useForm } from 'react-hook-form';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import React, { useEffect, useState } from 'react';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import { Col, Row } from 'react-flexbox-grid';
import { InsuranceContractTypeDto, InsuranceContractTypeIconDto } from 'api/insuranceContractTypes/models/InsuranceContractTypeDto';
import InsuranceContractTypesService from 'api/insuranceContractTypes/InsuranceContractTypesService';
import { useHistory, useParams } from 'react-router-dom';
import RenderSVG from 'common/components/renderSVG/RenderSVG';
import ReactTooltip from 'react-tooltip';

type Props = {
};

const InsuranceContractTypesDetails: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    // eslint-disable-next-line
    let { type, itemId } = useParams<{ type: string, itemId: string }>();
    const [insuranceContractType, setInsuranceContractType] = useState<InsuranceContractTypeDto | null>(null);
    const [defaultIcons, setDefaultIcons] = useState<InsuranceContractTypeIconDto[]>();
    const [iconSelected, setIconSelected] = useState<InsuranceContractTypeIconDto>();
    const [isDetails, setIsDetails] = useState<boolean>(true);
    const [isSystemItem, setIsSystemItem] = useState<boolean>(false);

    const [itemToRemove, setItemToRemove] = useState<InsuranceContractTypeDto>();
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = React.useState(false);

    const form = useForm<InsuranceContractTypeDto>({ shouldUnregister: false });
    const { register, handleSubmit, reset, errors } = form;

    const onSubmit = async (formData: InsuranceContractTypeDto) => {
        try {
            Loading.show();

            formData.iconSvg = iconSelected?.svg;

            if (insuranceContractType != null) {
                // For system items, keep the original name (formData.name is undefined when input is disabled)
                if (!isSystemItem) {
                    insuranceContractType.name = formData.name;
                }
                insuranceContractType.iconSvg = formData.iconSvg;
                await InsuranceContractTypesService.update(insuranceContractType);
            } else {
                itemId = await InsuranceContractTypesService.create(formData);
                getData();
            }

            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });

            history.goBack();
        } catch (error) {
            Loading.hide();

            if (error?.response?.status === 409) {
                addToast(t('common.messages.name_already_exists'), { appearance: 'warning' });
                return;
            }

            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update insurance contract type`,
                error
            );
        }
    };

    const getData = async () => {
        try {
            Loading.show();

            const icons = await InsuranceContractTypesService.catalogIcons();
            setDefaultIcons(icons);

            if (itemId) {
                const result = await InsuranceContractTypesService.getById(itemId);

                if (result.isSystem) {
                    result.name = t('common.insurance_contract_type.' + result.name as any);
                    setIsSystemItem(true);
                }

                setInsuranceContractType(result);
                reset(result);

                if (result.iconSvg && icons?.length) {
                    const selectedIcon = icons.find(x => x.svg === result.iconSvg);
                    setIconSelected(selectedIcon);
                }
            }

            Loading.hide();
        }
        catch (error) {
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't get insurance contract type`,
                error
            );
            Loading.hide();
        }
    }

    const showRemoveItemDialog = (item: InsuranceContractTypeDto) => {
        setItemToRemove(item);
        setDialogDeleteItemIsOpen(true);
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!!itemToRemove) {
            try {
                Loading.show();
                await InsuranceContractTypesService.remove(itemToRemove);
                addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
                history.push(`/settings/insuranceContractTypes/list`);
                Loading.hide();
            } catch (error) {
                addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        setIsDetails(type === 'details');
    }, [type]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [isSystemItem && !isDetails]);

    const onGoBack = () => {
        if (insuranceContractType) {
            reset(insuranceContractType);
            if (insuranceContractType.iconSvg && defaultIcons?.length) {
                const selectedIcon = defaultIcons.find(x => x.svg === insuranceContractType.iconSvg);
                setIconSelected(selectedIcon);
            }
        }
        history.push('/settings/insuranceContractTypes/list');
    }

    const IconSelector = () => {
        return <div style={{ display: 'flex' }}>
            {defaultIcons?.map(icon => <div
                key={icon.name}
                onClick={() => isDetails ? undefined : setIconSelected(icon)}
                className={`${styles.iconDiv} ${isDetails ? styles.disabled : ''} ${iconSelected?.name === icon.name ? styles.selected : ''}`}>
                <RenderSVG
                    item={icon.svg}
                    className={styles.icon}
                />
            </div>)}
        </div>
    }

    return (
        <div>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => history.push(`/settings/insuranceContractTypes/list`)} >
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('settings.insurance_types')}
                    </span>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <Col xs={12} sm={6}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('insurance_types.name')} {type !== 'details' ? '*' : ''}
                            </Label>
                            <Input
                                name='name'
                                placeholder={t('insurance_types.name')}
                                defaultValue={insuranceContractType?.name}
                                ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                disabled={type === 'details' || isSystemItem}
                                data-for={'name'}
                                data-tip={(isSystemItem && !isDetails) ? t('insurance_types.name_disabled_message') : undefined}
                            />
                            <InputError error={errors.name} maxLength={250} />
                            <ReactTooltip id="name" />
                        </FormItem>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('insurance_types.icon')}
                            </Label>
                            {IconSelector()}
                        </FormItem>
                    </Col>
                </Row>

                <div className={styles.buttonContainer}>
                    <Button preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onGoBack()} />
                    {type === 'details' &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            disabled={isSystemItem}
                            disabledMessage={isSystemItem ? t('insurance_types.forbidden_delete_tooltip') : undefined}
                            onClick={() => showRemoveItemDialog({ id: insuranceContractType?.id } as InsuranceContractTypeDto)} />
                    }
                    {type === 'details' && <Button type='button' text={t('common.edit')} onClick={() => history.push(`/settings/insuranceContractTypes/edit/${itemId}`)} />}
                    {type !== 'details' && <Button type='submit' text={t('common.save')} />}

                    <QuestionYesNo message={t('common.messages.remove_insurance_type', { name: insuranceContractType?.name ?? '' })}
                        isVisible={dialogDeleteItemIsOpen}
                        onYes={() => removeItem()}
                        onNo={() => setDialogDeleteItemIsOpen(false)} />
                </div>
            </form>
        </div>
    );
};

export default InsuranceContractTypesDetails;
